import { useMemo } from "react";
import {
  useGetTypeCategoryAndTags,
  convertTagDocsDataToOptions,
} from "../../../../../../DynamicTagsInputs/utils";

/**
 * Groups applied tags by their parent subTheme.
 * Returns an array of { subThemeDisplay, tags } objects,
 * or null if disabled or no data.
 *
 * Tags not linked to any subTheme are grouped under "Tags".
 */
export const useGroupTagsBySubTheme = (tagsData, enabled) => {
  // Normalize to a strict boolean. react-query v3 treats `enabled: undefined`
  // as `true`, which would cause the inner queries to fire with a null tagType.
  const isEnabled = !!enabled;

  const tagsQuery = useGetTypeCategoryAndTags({
    tagTypeProp: isEnabled ? "tags" : null,
    enableDataFetch: isEnabled,
  });

  const subThemesQuery = useGetTypeCategoryAndTags({
    tagTypeProp: isEnabled ? "subThemes" : null,
    enableDataFetch: isEnabled,
  });

  return useMemo(() => {
    if (!isEnabled || !tagsData?.length) return null;

    const allTagDocs =
      tagsQuery.status === "success" && tagsQuery.data
        ? convertTagDocsDataToOptions(tagsQuery.data, "tags")
        : [];

    const allSubThemeDocs =
      subThemesQuery.status === "success" && subThemesQuery.data
        ? convertTagDocsDataToOptions(subThemesQuery.data, "tags")
        : [];

    if (!allTagDocs.length) return null;

    // Build lookups (carry taxonomyRole through so callers can split groups
    // into Thematics / Attributes for non-legacy anno-mode tenants).
    const subThemeMetaMap = {};
    allSubThemeDocs.forEach((st) => {
      subThemeMetaMap[st._id] = {
        display: st.display,
        taxonomyRole: st?.meta?.taxonomyRole,
      };
    });

    const tagDocMap = {};
    allTagDocs.forEach((t) => {
      tagDocMap[t._id] = t;
    });

    // Group applied tags by their subTheme
    const groups = {}; // subThemeId -> { subThemeDisplay, tags }
    const ungrouped = [];

    tagsData.forEach((appliedTag) => {
      const fullDoc = tagDocMap[appliedTag._id];
      const subThemes = fullDoc?.tags?.subThemes?.data;

      if (subThemes?.length) {
        const st = subThemes[0];
        if (!groups[st._id]) {
          const meta = subThemeMetaMap[st._id];
          groups[st._id] = {
            subThemeDisplay: meta?.display || st.display || st._id,
            taxonomyRole: meta?.taxonomyRole,
            tags: [],
          };
        }
        groups[st._id].tags.push(appliedTag);
      } else {
        ungrouped.push(appliedTag);
      }
    });

    const result = Object.values(groups);
    if (ungrouped.length) {
      result.push({ subThemeDisplay: "Tags", taxonomyRole: undefined, tags: ungrouped });
    }

    return result;
  }, [tagsData, tagsQuery.data, tagsQuery.status, subThemesQuery.data, subThemesQuery.status, isEnabled]);
};
